import type {Builder} from '@wix/document-services-types'
import type {PresetsWithVariables, PresetVariableData} from '../types'
import {getPresetData} from '../presets'
import type {IPresetsStylesTransformer} from './style-transformer'
import type {CreateExtArgs} from '@wix/document-manager-core'

export interface IPresetsManifestProcessor {
    processPresetsManifest(presetsDataPointers: string[], presets: PresetsWithVariables): Builder.Presets
}

export class PresetsManifestProcessor implements IPresetsManifestProcessor {
    constructor(
        private readonly createExtArgs: CreateExtArgs,
        private readonly presetsStylesTransformer: IPresetsStylesTransformer
    ) {}

    processPresetsManifest(presetsDataPointers: string[], presets: PresetsWithVariables): Builder.Presets {
        const manifestEntries = presetsDataPointers
            .map(presetId => {
                const manifest = this.processPresetManifest(presetId, presets)
                const presetData = getPresetData(this.createExtArgs, presetId)

                if (!(manifest && presetData)) {
                    return null
                }

                return [presetData.presetId, manifest] as const
            })
            .filter((entry): entry is [string, Builder.PresetItem] => entry !== null)

        return Object.fromEntries(manifestEntries)
    }

    private processPresetManifest(presetId: string, presets: PresetsWithVariables): Builder.PresetItem | null {
        const presetData = getPresetData(this.createExtArgs, presetId)
        const presetVariables: Record<string, PresetVariableData> = presets[presetData.presetId]

        if (!presetData) {
            return null
        }

        return {
            displayName: presetData.name,
            presetDefaults: {
                cssCustomProperties: this.getDefaultStyles(presetVariables)
            }
        }
    }

    private getDefaultStyles(presetVariables: Record<string, PresetVariableData>): Builder.StyleOverrides {
        if (!presetVariables) {
            return {}
        }

        return this.presetsStylesTransformer.transformPresetStyles(presetVariables)
    }
}
